import {
  RECIPE_SECTION_PARAM,
  RECIPE_SECTION_SLUG_RE,
  recipeSectionInternalPath,
} from "./recipeSections";
import {
  recipePathAllowsPending,
  recipePathWithOpenAccess,
} from "./recipeOpenAccess";
import { JOURNEY_CODE_PARAM, JOURNEY_CODE_RE, journeyCodeFromSearch } from "./journeyLinks";

export const COMMUNITY_LINK_HOST = "dietasocial.it";
// The app's own URL scheme (iOS CFBundleURLSchemes).
export const APP_URL_SCHEME = "it.dietasocial.web";
export const PENDING_COMMUNITY_LINK_KEY = "community:pendingExternalPost";
export const PENDING_COMMUNITY_LINK_TTL_MS = 24 * 60 * 60 * 1000;
export const APP_HOME_PUBLIC_PATH = "/app";
export const APP_HOME_INTERNAL_PATH = "/home";
export const JOURNEYS_PUBLIC_PATH = "/percorsi";
// Where the VERIFICA EMAIL / ACCEDI button of a sign-in email opens.
export const EMAIL_VERIFICATION_INTERNAL_PATH = "/verifica-email";
export const JOURNEYS_INTERNAL_PATH =
  "/preferenze-nutrizionali?subtab=percorsi";
// One percorso's window on that tab: /percorsi?percorso=<code> (journeyLinks).
const JOURNEY_INTERNAL_RE = new RegExp(
  `^/preferenze-nutrizionali\\?subtab=percorsi&${JOURNEY_CODE_PARAM}=([a-z0-9-]+)$`,
);
const journeysPath = (searchParams) => {
  const code = journeyCodeFromSearch(`?${searchParams.toString()}`);
  return code
    ? `${JOURNEYS_INTERNAL_PATH}&${JOURNEY_CODE_PARAM}=${code}`
    : JOURNEYS_INTERNAL_PATH;
};
/** The Percorsi tab, with or without a percorso to open. */
export const isJourneysPath = (path) =>
  path === JOURNEYS_INTERNAL_PATH || JOURNEY_INTERNAL_RE.test(String(path || ""));
// dietasocial.it/app?sezione=<name> opens one section of the app. A query on
// /app, which iOS and Android already hand to the app: no association file or
// store release needed. First use: «Scopri gli specialisti» on the aDòc landing
// (client, 08/10/2026) → the Shop's «Consulti specialistici» (section adoc).
export const APP_SECTION_PARAM = "sezione";
export const CONSULTI_INTERNAL_PATH = "/shopping-widget/section/adoc";
// dietasocial.it/app?sezione=gratis opens the FREE 7-day Program page, for
// emails, WhatsApp, the minisites and social media (client, 15/10/2026).
export const FREE_PROGRAM_INTERNAL_PATH = "/percorso-gratuito";
const APP_SECTIONS = {
  consulti: CONSULTI_INTERNAL_PATH,
  gratis: FREE_PROGRAM_INTERNAL_PATH,
};

const appSectionPath = (searchParams) => {
  const section = String(searchParams.get(APP_SECTION_PARAM) || "").toLowerCase();
  return Object.prototype.hasOwnProperty.call(APP_SECTIONS, section)
    ? APP_SECTIONS[section]
    : null;
};

// dietasocial.it/app?pagina=<screen> opens one screen of the CRM's destination
// catalogue, the same list push notifications use (adoc
// config/app-destinations.php): the Magazine digitale's buttons, e.g. Shop →
// Abbonamenti or the Community (client, 26/10/2026). Only these screens are
// accepted. Keep the list in step with that catalogue and with the website's
// /app page (deployment/universal-links/app-home-open.html, PAGES).
export const APP_PAGE_PARAM = "pagina";
export const APP_PAGES = [
  "/home",
  "/community",
  "/notifiche",
  "/me",
  "/chat",
  "/ricette",
  "/video",
  "/multimedia",
  "/multimedia/preferiti",
  "/sfide",
  "/menu",
  "/il-mio-percorso",
  "/il-mio-percorso/storico",
  "/il-mio-percorso/prima-dopo",
  "/diario",
  "/misure-e-progressi",
  "/preferenze-nutrizionali",
  "/paywall",
  "/prodotti",
  "/percorso-gratuito",
  "/shopping-widget",
  "/shopping-widget/section/masterclub",
  "/shopping-widget/section/adoc",
  "/shopping-widget/section/wellness",
  "/impostazioni-profilo",
  "/imposta-notifiche",
  "/saldo-monete",
];
// The catalogue's «Video» has no screen of its own: its footer tab is /multimedia.
const APP_PAGE_ALIASES = { "/video": "/multimedia" };
const APP_PAGE_TARGETS = APP_PAGES.map((page) => APP_PAGE_ALIASES[page] || page);

const appPagePath = (searchParams) => {
  const page = String(searchParams.get(APP_PAGE_PARAM) || "")
    .trim()
    .toLowerCase()
    .replace(/\/+$/, "");
  if (!APP_PAGES.includes(page)) return null;
  return APP_PAGE_ALIASES[page] || page;
};

export const RECIPE_PATH_RE = /^\/recipe\/([1-9]\d*)$/;
// Where a Recipe Book section link opens: Ricette with that section's chip.
const RECIPE_SECTION_INTERNAL_RE = new RegExp(
  `^/ricette\\?${RECIPE_SECTION_PARAM}=([a-z0-9-]+)$`,
);

const INTERNAL_POST_PATH_RE = /^\/gruppi\/(\d+)\/(\d+)$/;
// The backend makes link tokens of 48 letters and digits.
const EMAIL_LINK_TOKEN_RE = /^[A-Za-z0-9]{20,120}$/;

const normalizePublicDietaSocialUrl = (rawUrl) => {
  if (!rawUrl) return null;

  try {
    const url = new URL(String(rawUrl));

    // The website's /app page reopens the app through its own scheme when the
    // browser didn't hand the link to the app: an in-app browser (Gmail's on
    // iPhone), or Safari on dietasocial.it itself, which keeps same-domain links.
    // Only the sign-in link, the app sections and the percorsi are accepted this way.
    if (url.protocol === `${APP_URL_SCHEME}:`) {
      if (url.hostname === "recipe" && /^\/[1-9]\d*$/.test(url.pathname)) {
        return recipePathWithOpenAccess(`/recipe${url.pathname}`, url.searchParams);
      }
      if (url.hostname === "percorsi" && /^\/?$/.test(url.pathname)) {
        return journeysPath(url.searchParams);
      }
      if (url.hostname !== "app") return null;
      const verifica = url.searchParams.get("verifica");
      if (verifica && EMAIL_LINK_TOKEN_RE.test(verifica)) {
        return `${EMAIL_VERIFICATION_INTERNAL_PATH}?verifica=${verifica}`;
      }
      return appSectionPath(url.searchParams) || appPagePath(url.searchParams);
    }

    if (url.protocol !== "https:" || url.hostname !== COMMUNITY_LINK_HOST) {
      return null;
    }

    const pathname = url.pathname.replace(/\/+$/, "");
    if (pathname === APP_HOME_PUBLIC_PATH) {
      // The same address carries the sign-in email's button, with its token.
      const verifica = url.searchParams.get("verifica");
      if (verifica && EMAIL_LINK_TOKEN_RE.test(verifica)) {
        return `${EMAIL_VERIFICATION_INTERNAL_PATH}?verifica=${verifica}`;
      }
      return (
        appSectionPath(url.searchParams) ||
        appPagePath(url.searchParams) ||
        APP_HOME_INTERNAL_PATH
      );
    }

    if (pathname === JOURNEYS_PUBLIC_PATH) {
      return journeysPath(url.searchParams);
    }

    if (RECIPE_PATH_RE.test(pathname)) {
      return recipePathWithOpenAccess(pathname, url.searchParams);
    }

    // /recipe/<section>, e.g. /recipe/secondi-di-pesce (client, 30/09/2026).
    const sectionMatch = pathname.match(/^\/recipe\/([^/]+)$/);
    if (sectionMatch) {
      const slug = sectionMatch[1].toLowerCase();
      return RECIPE_SECTION_SLUG_RE.test(slug)
        ? recipeSectionInternalPath(slug)
        : null;
    }

    const match = pathname.match(INTERNAL_POST_PATH_RE);
    if (!match) return null;
    return `/gruppi/${match[1]}/${match[2]}`;
  } catch {
    return null;
  }
};

export const normalizeDietaSocialUrl = normalizePublicDietaSocialUrl;

export const normalizeCommunityPostUrl = (rawUrl) => {
  const path = normalizePublicDietaSocialUrl(rawUrl);
  return INTERNAL_POST_PATH_RE.test(String(path || "")) ? path : null;
};

const isSupportedPendingPath = (path) =>
  path === APP_HOME_INTERNAL_PATH ||
  (isJourneysPath(path) &&
    (path === JOURNEYS_INTERNAL_PATH || JOURNEY_CODE_RE.test(path.match(JOURNEY_INTERNAL_RE)[1]))) ||
  Object.values(APP_SECTIONS).includes(path) ||
  APP_PAGE_TARGETS.includes(path) ||
  INTERNAL_POST_PATH_RE.test(path) ||
  recipePathAllowsPending(path) ||
  (RECIPE_SECTION_INTERNAL_RE.test(path) &&
    RECIPE_SECTION_SLUG_RE.test(path.match(RECIPE_SECTION_INTERNAL_RE)[1]));

export const savePendingCommunityPath = (
  path,
  now = Date.now(),
  storage = globalThis?.localStorage,
) => {
  const normalizedPath = String(path || "");
  if (!isSupportedPendingPath(normalizedPath) || !storage) {
    return false;
  }
  storage.setItem(
    PENDING_COMMUNITY_LINK_KEY,
    JSON.stringify({ path, createdAt: now }),
  );
  return true;
};

export const readPendingCommunityPath = (
  now = Date.now(),
  storage = globalThis?.localStorage,
) => {
  if (!storage) return null;

  try {
    const raw = storage.getItem(PENDING_COMMUNITY_LINK_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw);
    const isValidPath = isSupportedPendingPath(String(pending?.path || ""));
    const isFresh =
      Number.isFinite(pending?.createdAt) &&
      now - pending.createdAt <= PENDING_COMMUNITY_LINK_TTL_MS;
    if (!isValidPath || !isFresh) {
      storage.removeItem(PENDING_COMMUNITY_LINK_KEY);
      return null;
    }
    return pending.path;
  } catch {
    storage.removeItem(PENDING_COMMUNITY_LINK_KEY);
    return null;
  }
};

export const clearPendingCommunityPath = (
  storage = globalThis?.localStorage,
) => {
  storage?.removeItem(PENDING_COMMUNITY_LINK_KEY);
};

export const buildCommunityPostFocusLocation = (postId) => {
  const normalizedPostId = String(postId || "");
  if (!/^\d+$/.test(normalizedPostId)) {
    return { pathname: "/community" };
  }

  return {
    pathname: "/community",
    search: `?focusPost=${normalizedPostId}&focusAnchor=1`,
    state: {
      focusPostId: normalizedPostId,
      focusAnchor: true,
    },
  };
};
